// 走子生成 — 7 种棋子第一性原理实现
// 不引用 xiangqi.js; 严格遵循 plan.md Phase 0 "关键走子规则" 与反模式表.

import {
  Color, GameState, Move, Piece, PieceType, Square,
  crossedRiver, inPalace, inBounds, makeSquare, fileOf, rankOf,
  NUM_SQUARES,
} from './types';
import { startState } from './fen';

type Board = (Piece | null)[];

// 加走法到数组 (目标格必须为空或是敌子)
function tryAdd(board: Board, color: Color, from: Square, toFile: number, toRank: number, out: Move[]): boolean {
  if (!inBounds(toFile, toRank)) return false;
  const to = makeSquare(toFile, toRank);
  const target = board[to];
  if (target && target.color === color) return false; // 友子阻挡
  out.push({ from, to, captured: target ?? undefined });
  return target === null; // 若空格则后续可继续延伸; 若吃子则停 (调用者按返回值处理)
}

// 帅/将: 1 步正交, 限九宫
function genKing(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  for (const [df, dr] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
    const nf = f + df, nr = r + dr;
    if (!inPalace(color, nf, nr)) continue;
    tryAdd(state.board, color, sq, nf, nr, out);
  }
}

// 仕/士: 1 步对角, 限九宫
function genAdvisor(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  for (const [df, dr] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
    const nf = f + df, nr = r + dr;
    if (!inPalace(color, nf, nr)) continue;
    tryAdd(state.board, color, sq, nf, nr, out);
  }
}

// 相/象: 田字走 2 格, 象眼需空, 不过河
function genElephant(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  for (const [df, dr] of [[2, 2], [2, -2], [-2, 2], [-2, -2]]) {
    const nf = f + df, nr = r + dr;
    if (!inBounds(nf, nr)) continue;
    // 不过河: 红象目标 rank 必须 0-4; 黑象目标 rank 必须 5-9
    if (color === 'r' && nr >= 5) continue;
    if (color === 'b' && nr <= 4) continue;
    // 象眼 = 中点
    const ef = f + df / 2, er = r + dr / 2;
    if (state.board[makeSquare(ef, er)] !== null) continue;
    tryAdd(state.board, color, sq, nf, nr, out);
  }
}

// 马: 日字 8 方向, 蹩马腿(长腿方向的正交相邻格需空)
function genHorse(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  // [df, dr, legDf, legDr] — legDf/legDr 是长腿方向 (2 格那条) 的正交相邻格
  const candidates: Array<[number, number, number, number]> = [
    [1, 2, 0, 1],   // 向下 2 右 1, 长腿 = 下 (0,+1)
    [-1, 2, 0, 1],  // 向下 2 左 1, 长腿 = 下
    [1, -2, 0, -1],
    [-1, -2, 0, -1],
    [2, 1, 1, 0],   // 向右 2 下 1, 长腿 = 右
    [2, -1, 1, 0],
    [-2, 1, -1, 0],
    [-2, -1, -1, 0],
  ];
  for (const [df, dr, lf, lr] of candidates) {
    const nf = f + df, nr = r + dr;
    if (!inBounds(nf, nr)) continue;
    // 蹩马腿
    if (state.board[makeSquare(f + lf, r + lr)] !== null) continue;
    tryAdd(state.board, color, sq, nf, nr, out);
  }
}

// 车: 4 方向滑动
function genChariot(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  for (const [df, dr] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
    let nf = f + df, nr = r + dr;
    while (inBounds(nf, nr)) {
      const wasEmpty = tryAdd(state.board, color, sq, nf, nr, out);
      if (!wasEmpty) break; // 吃子或友子停
      nf += df; nr += dr;
    }
  }
}

// 炮: 走子如车; 吃子需恰好 1 个炮架
function genCannon(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  for (const [df, dr] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
    let nf = f + df, nr = r + dr;
    // 阶段 1: 走子 (空格), 直到遇到第一个棋子=炮架
    while (inBounds(nf, nr) && state.board[makeSquare(nf, nr)] === null) {
      tryAdd(state.board, color, sq, nf, nr, out); // 走到空格
      nf += df; nr += dr;
    }
    if (!inBounds(nf, nr)) continue; // 整条线无子
    // 此处 (nf,nr) 是炮架 (任意棋子, 友敌皆可)
    // 阶段 2: 跳过炮架, 继续找下一个棋子
    let ff = nf + df, fr = nr + dr;
    while (inBounds(ff, fr) && state.board[makeSquare(ff, fr)] === null) {
      ff += df; fr += dr;
    }
    if (!inBounds(ff, fr)) continue;
    const target = state.board[makeSquare(ff, fr)];
    if (target && target.color !== color) {
      // 跨 1 炮架吃敌
      out.push({ from: sq, to: makeSquare(ff, fr), captured: target });
    }
  }
}

// 兵/卒: 向前 1 格; 过河后可加 file ±1; 永不后退, 不斜吃
function genPawn(state: GameState, sq: Square, color: Color, out: Move[]): void {
  const f = fileOf(sq), r = rankOf(sq);
  // 红向前 = rank +1; 黑向前 = rank -1
  const forward = color === 'r' ? r + 1 : r - 1;
  if (inBounds(f, forward)) tryAdd(state.board, color, sq, f, forward, out);
  // 过河后可左右
  if (crossedRiver(color, r)) {
    if (inBounds(f + 1, r)) tryAdd(state.board, color, sq, f + 1, r, out);
    if (inBounds(f - 1, r)) tryAdd(state.board, color, sq, f - 1, r, out);
  }
}

const TYPE_TO_GEN: Record<PieceType, (s: GameState, sq: Square, c: Color, out: Move[]) => void> = {
  K: genKing, A: genAdvisor, B: genElephant, N: genHorse,
  R: genChariot, C: genCannon, P: genPawn,
};

// 伪合法走子 (未考虑走出后己方是否被将军/飞将)
export function generatePseudoMoves(state: GameState): Move[] {
  const out: Move[] = [];
  const color = state.turn;
  for (let sq = 0; sq < NUM_SQUARES; sq++) {
    const p = state.board[sq];
    if (!p || p.color !== color) continue;
    TYPE_TO_GEN[p.type](state, sq, color, out);
  }
  return out;
}

// 单棋子伪合法走子 (UI 用)
export function generatePieceMoves(state: GameState, from: Square): Move[] {
  const p = state.board[from];
  if (!p || p.color !== state.turn) return [];
  const out: Move[] = [];
  TYPE_TO_GEN[p.type](state, from, p.color, out);
  return out;
}

// 缓存: 起手合法走法数测试 (后面 rules.ts 会过滤将军)
// 起手伪合法数 (无任何走子) — 用于早期 sanity check
export function startPseudoMoveCount(): number {
  return generatePseudoMoves(startState()).length;
}
